import type { ClassSeries, IsoDate } from './types';

/**
 * The two top-level views of the Classes list. `active` is the default: the
 * classes that still matter day to day. `past` is everything finished —
 * archived, or run out of dates.
 */
export type SeriesTab = 'active' | 'past';

export const SERIES_TABS: readonly SeriesTab[] = ['active', 'past'];

export const SERIES_TAB_LABELS: Readonly<Record<SeriesTab, string>> = {
  active: 'Active',
  past: 'Past',
};

/**
 * A refinement within either tab. One-time and recurring split by rule; `sub`
 * cuts across both (a sub class can be either) by the {@link ClassSeries.isSub}
 * label.
 */
export type SeriesTypeFilter = 'all' | 'once' | 'recurring' | 'sub';

/** Display order of the type-filter chips. */
export const SERIES_TYPE_FILTERS: readonly SeriesTypeFilter[] = ['all', 'once', 'recurring', 'sub'];

export const SERIES_TYPE_FILTER_LABELS: Readonly<Record<SeriesTypeFilter, string>> = {
  all: 'All',
  once: 'One-time',
  recurring: 'Recurring',
  sub: 'Sub',
};

/**
 * A series is "past" when it can no longer put anything on the schedule: it was
 * archived, its one-time date has gone by, or its last date has passed. A class
 * happening *today* is not past — the instructor still has to teach it.
 *
 * `today` is a parameter rather than read from a clock so this stays pure and
 * the caller decides whose "today" applies. ISO dates compare correctly as
 * plain strings.
 */
export function isSeriesPast(series: ClassSeries, today: IsoDate): boolean {
  if (!series.active) {
    return true;
  }
  if (series.recurrence.freq === 'once') {
    return series.startDate < today;
  }
  return series.endDate !== null && series.endDate < today;
}

/** Whether a series belongs on the given tab, refined by the type filter. */
export function matchesSeriesView(
  series: ClassSeries,
  tab: SeriesTab,
  type: SeriesTypeFilter,
  today: IsoDate,
): boolean {
  const past = isSeriesPast(series, today);
  if (tab === 'active' ? past : !past) {
    return false;
  }
  switch (type) {
    case 'all':
      return true;
    case 'once':
      return series.recurrence.freq === 'once';
    case 'recurring':
      return series.recurrence.freq !== 'once';
    case 'sub':
      return series.isSub;
    default: {
      const exhaustive: never = type;
      throw new Error(`Unsupported filter: ${String(exhaustive)}`);
    }
  }
}
